import { Injectable } from '@angular/core';

/**
 * Service to manage scroll position preservation for mobile view navigation.
 * Saves and restores scroll positions when navigating between screens,
 * ensuring users return to their previous scroll position when using the back button.
 * 
 * Active on mobile view (width < 768px) for all list routes, and on desktop
 * for `/receiver`, `/scheduler`, `/growers`, `/transloading`, `/gatehouse`, `/shunting`, and `/audit` so those lists restore window scroll after passport details.
 */
@Injectable({ providedIn: 'root' })
export class ScrollPositionService {
  private readonly scrollPositions = new Map<string, number>();
  private readonly MOBILE_BREAKPOINT = 768;
  
  /**
   * Flag to track if we're returning from a detail page.
   * Used to skip data reload and preserve scroll position.
   */
  private isReturningFromDetail = false;

  /**
   * Set the returning from detail flag.
   * Call this before navigating to a detail page (passport details, comments, history).
   * @param value - Whether we're about to navigate to a detail page
   */
  setReturningFromDetail(value: boolean): void {
    this.isReturningFromDetail = value;
  }

  /**
   * Check if we're returning from a detail page and auto-reset the flag.
   * This ensures the flag is only used once per navigation.
   * @returns true if returning from a detail page
   */
  isReturning(): boolean {
    const returning = this.isReturningFromDetail;
    this.isReturningFromDetail = false; // Auto-reset after check
    return returning;
  }

  /**
   * Check if we're in mobile view
   */
  private isMobileView(): boolean {
    return globalThis.window !== undefined && globalThis.window.innerWidth < this.MOBILE_BREAKPOINT;
  }

  /**
   * Receiver, Scheduler, Growers, Transloading, Gatehouse, Shunting, and Audit lists restore scroll on desktop and mobile; other routes only on mobile.
   */
  private shouldSaveOrRestoreScrollForRoute(route: string): boolean {
    return (
      route === '/receiver' ||
      route === '/scheduler' ||
      route === '/growers' ||
      route === '/transloading' ||
      route === '/gatehouse' ||
      route === '/shunting' ||
      route === '/audit' ||
      this.isMobileView()
    );
  }

  /**
   * Save the current scroll position for a given route.
   * Saves on mobile for all routes, and on desktop for list personas above (including /scheduler) only.
   * @param route - The route identifier (e.g., '/growers', '/scheduler')
   */
  saveScrollPosition(route: string): void {
    if (!this.shouldSaveOrRestoreScrollForRoute(route)) {
      return;
    }

    const scrollY = globalThis.window === undefined
      ? 0
      : (globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0);
    
    this.scrollPositions.set(route, scrollY);
  }

  /**
   * Restore the saved scroll position for a given route.
   * Uses setTimeout to ensure DOM has updated before scrolling.
   * Restores on mobile for all routes; on desktop for the same list routes as saveScrollPosition.
   * @param route - The route identifier
   * @param delay - Optional delay in ms before restoring (default: 100)
   */
  restoreScrollPosition(route: string, delay: number = 100): void {
    if (!this.shouldSaveOrRestoreScrollForRoute(route)) {
      return;
    }

    const savedPosition = this.scrollPositions.get(route);
    if (savedPosition !== undefined && savedPosition > 0) {
      setTimeout(() => {
        if (globalThis.window !== undefined) {
          globalThis.window.scrollTo({
            top: savedPosition,
            behavior: 'instant'
          });
        }
      }, delay);
    }
  }

  /**
   * Restore the saved scroll position after data has been loaded and rendered.
   * Uses requestAnimationFrame to ensure the DOM has been painted before scrolling.
   * This method should be called AFTER async data loading completes.
   * Restores on mobile for all routes; on desktop for the same list routes as saveScrollPosition.
   * @param route - The route identifier
   * @param delay - Optional delay in ms after RAF before restoring (default: 150)
   */
  restoreScrollPositionAfterDataLoad(route: string, delay: number = 150): void {
    if (!this.shouldSaveOrRestoreScrollForRoute(route)) {
      return;
    }

    const savedPosition = this.scrollPositions.get(route);
    if (savedPosition === undefined || savedPosition <= 0) {
      return;
    }

    // Use requestAnimationFrame to ensure DOM has been painted,
    // then add a small delay to ensure Angular change detection has completed
    if (globalThis.window !== undefined && typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(() => {
        setTimeout(() => {
          if (globalThis.window !== undefined) {
            globalThis.window.scrollTo({
              top: savedPosition,
              behavior: 'instant'
            });
          }
        }, delay);
      });
    } else {
      // Fallback for environments without requestAnimationFrame
      setTimeout(() => {
        if (globalThis.window !== undefined) {
          globalThis.window.scrollTo({
            top: savedPosition,
            behavior: 'instant'
          });
        }
      }, delay + 16); // Add ~1 frame delay as fallback
    }
  }

  /**
   * Check if there's a saved scroll position for a route
   * @param route - The route identifier
   * @returns true if a scroll position is saved for this route
   */
  hasScrollPosition(route: string): boolean {
    return this.scrollPositions.has(route);
  }

  /**
   * Get the saved scroll position for a route without restoring it
   * @param route - The route identifier
   * @returns The saved scroll position or undefined
   */
  getScrollPosition(route: string): number | undefined {
    return this.scrollPositions.get(route);
  }

  /**
   * Clear the saved scroll position for a route.
   * Call this when you want to reset the scroll position (e.g., on logout, data refresh).
   * @param route - The route identifier
   */
  clearScrollPosition(route: string): void {
    this.scrollPositions.delete(route);
  }

  /**
   * Clear all saved scroll positions.
   * Useful when logging out or resetting the app state.
   */
  clearAllScrollPositions(): void {
    this.scrollPositions.clear();
  }
}
